import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { DeliveryService, DeliveryType } from "../../../common/types/enums";
import {
  CartItem,
  CdekCalculateRequest,
  CdekCalculateResponse,
  CdekPickupPoint,
  CdekTariff,
} from "../../../common/types/interfaces";
import { getApi } from "../../../api/api";
import { selectClusterList } from "../../../store/slices/clusterListSlice";
import { selectCart } from "../../../store/slices/cartSlice";

interface UseCdekDeliveryProps {
  deliveryType: DeliveryType;
  cityId: number | null;
  storeId: number | null;
  addressId: number | null;
  clusterId: number;
}

export const useCdekDelivery = ({
  deliveryType,
  storeId,
  cityId,
  addressId,
  clusterId,
}: UseCdekDeliveryProps) => {
  const api = getApi();
  const cart = useSelector(selectCart);
  const clusters = useSelector(selectClusterList);
  const currentCluster = clusters.find((c) => Number(c.id) === clusterId);

  const cartItems: CartItem[] = useMemo(
    () => cart.items.filter((i) => i.clusterId === clusterId && !i.isGift),
    [cart.items, clusterId],
  );

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<CdekCalculateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedTariff, setSelectedTariff] = useState<CdekTariff | null>(null);
  const [selectedPickupPoint, setSelectedPickupPoint] =
    useState<CdekPickupPoint | null>(null);
  const [deliveryService, setDeliveryService] = useState<DeliveryService>(
    DeliveryService.CUSTOM,
  );
  const [cdekTariffCode, setCdekTariffCode] = useState<number | undefined>(
    undefined,
  );

  const applyMarkup = (sum: number): number => {
    const markup = Number(currentCluster?.cdek?.markup) || 0;
    if (markup === 0) return sum;
    const multiplier = 1 + markup / 100;
    return Math.ceil(sum * multiplier);
  };

  useEffect(() => {
    const isCdekEnabled = currentCluster?.cdek?.enabled ?? false;

    if (
      deliveryType !== DeliveryType.DELIVERY ||
      !isCdekEnabled ||
      !storeId ||
      !cityId ||
      !addressId ||
      !cartItems.length
    ) {
      setOptions(null);
      setError(null);
      setSelectedTariff(null);
      setSelectedPickupPoint(null);
      setDeliveryService(DeliveryService.CUSTOM);
      setCdekTariffCode(undefined);
      return;
    }

    const packages: CdekCalculateRequest["packages"] = [];
    let hasError = false;

    for (const item of cartItems) {
      const weightAttr = item.attributes?.find(
        (a) => a.name.toLowerCase() === "вес",
      );
      const lengthAttr = item.attributes?.find(
        (a) => a.name.toLowerCase() === "длина",
      );
      const widthAttr = item.attributes?.find(
        (a) => a.name.toLowerCase() === "ширина",
      );
      const heightAttr = item.attributes?.find(
        (a) => a.name.toLowerCase() === "высота",
      );

      if (!weightAttr || !lengthAttr || !widthAttr || !heightAttr) {
        setError(`У товара "${item.name}" отсутствуют габариты`);
        setOptions(null);
        setSelectedTariff(null);
        setSelectedPickupPoint(null);
        setDeliveryService(DeliveryService.CUSTOM);
        setCdekTariffCode(undefined);
        hasError = true;
        break;
      }

      packages.push({
        weight: parseFloat(String(weightAttr.value)),
        length: parseFloat(String(lengthAttr.value)),
        width: parseFloat(String(widthAttr.value)),
        height: parseFloat(String(heightAttr.value)),
      });
    }

    if (hasError) return;

    const request: CdekCalculateRequest = {
      storeId,
      addressId,
      packages,
    };

    const fetchDelivery = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.cdekCalculate(request);
        setOptions(response);
      } catch (err: any) {
        setError(err.message || "Ошибка расчёта доставки СДЭК");
        setOptions(null);
        setSelectedTariff(null);
        setSelectedPickupPoint(null);
        setDeliveryService(DeliveryService.CUSTOM);
        setCdekTariffCode(undefined);
      } finally {
        setLoading(false);
      }
    };

    fetchDelivery();
  }, [
    deliveryType,
    storeId,
    cityId,
    addressId,
    cartItems,
    currentCluster,
    api,
  ]);

  const filteredTariffs = useMemo(() => {
    if (!options?.tariffs?.length) return [];
    const cdekSettings = currentCluster?.cdek;
    if (!cdekSettings) return options.tariffs;

    return options.tariffs.filter((t) => {
      if (t.delivery_mode === 1) return cdekSettings.courier;
      if (t.delivery_mode === 2 || t.delivery_mode === 7)
        return cdekSettings.pickup;
      return false;
    });
  }, [options, currentCluster]);

  useEffect(() => {
    if (filteredTariffs.length) {
      setSelectedTariff(null);
      setSelectedPickupPoint(null);
      setDeliveryService(DeliveryService.CUSTOM);
      setCdekTariffCode(undefined);
    }
  }, [filteredTariffs]);

  return {
    loading,
    options:
      filteredTariffs.length && options
        ? { ...options, tariffs: filteredTariffs }
        : null,
    error,
    selectedTariff,
    selectedPickupPoint,
    setSelectedTariff,
    setSelectedPickupPoint,
    deliveryService,
    cdekTariffCode,
    setDeliveryService,
    setCdekTariffCode,
    applyMarkup,
  };
};
